import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { windowIsDefined } from '@klv/shared/utils/functions';
import { SelectArrow } from '@klv/molecules/form-field/select';
import { Input, Select } from '@klv/molecules/form-field';
import Toggle from '@klv/molecules/toggle';
import Typography from '@klv/atoms/typography';

import * as s from './styles';
import { Dropdown, StyledLabel, FlexContainer } from '../styles';
import type { IPricingCalc } from './types';

import pricingData from '@klv/shared/pricing/static/us.json';
import creditRatios from '@klv/shared/pricing/static/credit-ratio.json';
import carrierFees from '@klv/shared/pricing/static/carrier-fees.json';

const smsCreditRatios = creditRatios.sms;
const mmsCreditRatios = creditRatios.mms;
const smsCarrierFees = carrierFees.sms;
const mmsCarrierFees = carrierFees.mms;

type TSmsRatios = keyof typeof creditRatios['sms'];

type TMmsRatios = keyof typeof creditRatios['mms'];

type TSmsCarrierFees = keyof typeof carrierFees['sms'];

type TMmsCarrierFees = keyof typeof carrierFees['mms'];

type TValidLocales = TSmsRatios | TMmsRatios;

type TSmsPlans = keyof typeof pricingData['sms'];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [minDesktopWidth, setMinDesktopWidth] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth < 576;
      const isMinDesktopWidth = window.innerWidth > 1024 && window.innerWidth < 1280;

      setIsMobile(isMobileView);
      setMinDesktopWidth(isMinDesktopWidth);
    };

    if (windowIsDefined()) {
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  return { isMobile, minDesktopWidth };
};


export const PricingCalculator = ({ smsCredits, backgroundColor, isSms, setIsSms, selectedLocale, setSelectedLocale }: IPricingCalc) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  
  const creditType = isSms ? 'sms' : 'mms';
  const credits = useMemo(() => Object.keys(creditRatios[creditType]), [creditType]);
  const ratio: number = creditType === 'mms' 
    ? mmsCreditRatios[selectedLocale as TMmsRatios] 
    : smsCreditRatios[selectedLocale as TSmsRatios];
  
  const maxSends = Math.trunc(smsCredits / ratio);
  const price = pricingData.sms[smsCredits.toString() as TSmsPlans].price;
  const selectedFee: number = creditType === 'mms' 
    ? mmsCarrierFees[selectedLocale as TMmsCarrierFees] 
    : smsCarrierFees[selectedLocale as TSmsCarrierFees];

  const creditString = creditType === 'mms' ? t('organisms_pricing_calculator_mms') : t('organisms_pricing_calculator_sms');
  const { isMobile, minDesktopWidth } = useIsMobile();
  const TextComponent = (isMobile && maxSends.toString().length > 5) || minDesktopWidth ? s.MobileText : s.Text;

  return (
    <s.CalculatorWrapper>
      <s.ToggleWrapper>
        <Toggle
          wide
          rounded
          backgroundColor='white'
          falseText={t('organisms_pricing_calculator_mms')}
          setValue={() => setIsSms(!isSms)}
          value={isSms}
          trueText={t('organisms_pricing_calculator_sms')}
        />
        </s.ToggleWrapper>
      <FlexContainer>
        <StyledLabel variant="tagline" >{t('organisms_pricing_calculator_sendTo')}</StyledLabel>
        <Dropdown $backgroundColor={backgroundColor} >
          <SelectArrow $backgroundColor={backgroundColor} iconName="ChevronDown" fillColor="currentColor" />
          <Select $backgroundColor={backgroundColor} 
            aria-labelledby="pricing--sms-country"
            hasError={false}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setSelectedLocale(event.target.value as TValidLocales)}
            value={selectedLocale as TValidLocales}
          >
            {(credits).sort((a, b) => a.localeCompare(b)).map((code) => (
              <option key={code} value={code}>{t(`organisms_pricing_calculator_locales_${code}`, code)}</option>
            ))}

          </Select>
        </Dropdown>
      </FlexContainer>
      <FlexContainer>
        <s.Text variant="tagline">{t('organisms_pricing_calculator_maxSends')}</s.Text>
        <TextComponent variant='tagline'>
          <div style={{ alignSelf: 'end' }}><strong>{maxSends}</strong></div>
          <s.Credits variant='tagline'>
            ({ratio} {ratio === 1 ? t('organisms_pricing_calculator_credit') : t('organisms_pricing_calculator_credits')}/{creditString})
          </s.Credits>
        </TextComponent>
      </FlexContainer>
      <s.Divider/>
      <FlexContainer>
        <s.Text variant="tagline">{t('organisms_pricing_calculator_pricePer')} {creditString}</s.Text>
        <TextComponent variant='tagline'><strong>{
          (price / maxSends)
            .toLocaleString(locale, { style: 'currency', currency: 'USD', minimumFractionDigits: 4  })
          }</strong></TextComponent>
      </FlexContainer>
      {selectedFee 
        ? <Typography variant='disclaimer'>* {t('organisms_pricing_calculator_carrierFeeDisclaimer')} {selectedFee.toLocaleString(locale, { style: 'currency', currency: 'USD', minimumFractionDigits: 3  })}. {t('organisms_pricing_calculator_carrierFeeDisclaimerEnd')}</Typography> 
        : <Typography variant='disclaimer'>* {t('organisms_pricing_calculator_carrierFeeDisclaimerEnd')}</Typography>}
    </s.CalculatorWrapper>
  );
};
